import { Injectable } from '@angular/core';
import { IncidentRatioTrend, PreviousCoachingSession } from '@app-reports/models/reports.model';

@Injectable({
  providedIn: 'root',
})
export class CoachingTimelineService {
  private formatMonthYear = (date: Date): string => `${date.getFullYear()}-${date.getMonth() + 1}`;

  constructor() {}

  public getCoachingTimelineData(
    eventsPer100Miles: IncidentRatioTrend[],
    coachingSessionData: PreviousCoachingSession[]
  ): {
    extendedEventsPer100Miles: IncidentRatioTrend[];
    coachingSessionChartData: { date: string; coachingSessionId: number; value: number }[];
  } {
    // Add Date for caochedAt key for coachingSessionData.
    const previousCoachingSessionData: { coachedAt: Date; coachingSessionId: number }[] = coachingSessionData?.map((values) => {
      return {
        coachedAt: new Date(values.coachedAt),
        coachingSessionId: values.coachingSessionId,
      };
    });

    // Get dummy data for eventsPer100miles for 6 months if needed.
    const extendedEventsPer100Miles = this.addDummyDates(eventsPer100Miles);

    // Based on extendedEventsPer100Miles add points for annotations value(coachingSessionData)
    const coachingSessionChartData: { date: string; coachingSessionId: number; value: number }[] = [];

    previousCoachingSessionData?.forEach((previousCoachingSession, index) => {
      let between: number | null = null;
      const coachingMonth = this.findMonthIndex(extendedEventsPer100Miles, previousCoachingSession.coachedAt);
      if (coachingMonth === -1) {
        const coachingDay = new Date(previousCoachingSession.coachedAt).getDate();
        const points = (coachingDay - 1) / 60;
        between = coachingMonth - points;
      } else if (coachingMonth === extendedEventsPer100Miles.length - 1) {
        const coachingDay = new Date(previousCoachingSession.coachedAt).getDate();
        const points = (coachingDay - 1) / 60;
        between = coachingMonth + points;
      } else if (coachingMonth !== null) {
        const coachingDay = new Date(previousCoachingSession.coachedAt).getDate();
        const points = (coachingDay - 1) / 30;
        between = coachingMonth + points;
      }

      coachingSessionChartData.push({
        date: coachingSessionData[index].coachedAt,
        coachingSessionId: index + 1,
        value: between,
      });
    });

    return { extendedEventsPer100Miles, coachingSessionChartData };
  }

  // Getting index for annotation data using date of coachingSessionData
  private findMonthIndex(mergedResult: IncidentRatioTrend[], targetDate: Date): number {
    const targetMonthYear = this.formatMonthYear(targetDate);
    for (let i = 0; i < mergedResult.length; i++) {
      const resultDate = new Date(mergedResult[i].date);
      const resultMonthYear = this.formatMonthYear(resultDate);
      if (targetMonthYear < resultMonthYear && i == 0) {
        return -1;
      } else if (resultMonthYear === targetMonthYear) {
        return i;
      }
    }
    return null;
  }

  // Adding dummy dates if needed for 6 months
  private addDummyDates(events: IncidentRatioTrend[]): IncidentRatioTrend[] {
    const updatedEvents = events;

    // Get the current date and calculate the date 6 months ago
    const currentDate = new Date();
    const sixMonthsAgo = new Date(currentDate);
    sixMonthsAgo.setMonth(currentDate.getMonth() - 6);

    sixMonthsAgo.setDate(1);
    sixMonthsAgo.setHours(0, 0, 0, 0);
    currentDate.setDate(1);

    // Create a set to store unique months from existing events
    const existingMonths = new Set<string>();
    updatedEvents?.forEach((event) => {
      const eventDate = new Date(event.date);
      if (eventDate >= sixMonthsAgo && eventDate <= eventDate) {
        existingMonths.add(this.formatMonthYear(eventDate));
      }
    });

    // Initialize result with existing events
    const result: IncidentRatioTrend[] = [];

    // Generate dummy dates and insert them at the correct position
    let currentMonth = new Date(sixMonthsAgo);

    while (currentMonth <= currentDate) {
      // If the current month is not in existing events, add a dummy date
      if (!existingMonths.has(this.formatMonthYear(currentMonth))) {
        result.push({
          date: `${currentMonth}`,
          incidentRatio: null,
        });
      }
      // Move to the next month
      currentMonth.setMonth(currentMonth.getMonth() + 1);
    }

    if (updatedEvents?.length) {
      result.push(...updatedEvents);
    }
    const sortedEvents = result.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    return sortedEvents;
  }
}
